<template>
  <div class="exam-violation">
    <div v-if="loadError" class="load-error" role="alert">
      {{ loadError }} <el-button type="text" @click="loadData">Thử lại</el-button>
    </div>
    <el-tabs v-model="activeTab">
      <el-tab-pane label="Ghi nhận vi phạm" name="record">
        <ViolationFormSkeleton v-if="loading" />
        <ViolationForm v-else ref="violationForm" :students="students" :reasons="reasons"
                       :loading-reasons="loadingReasons" :reason-error="reasonError"
                       :submitting="submitting" :disabled="!!loadError"
                       @reason-query="loadReasons" @retry-reasons="loadReasons(reasonQuery)"
                       @submit="recordViolation" @cancel="$emit('closeDialog')" />
      </el-tab-pane>
      <el-tab-pane label="Lịch sử xử lý" name="history">
        <ViolationHistorySkeleton v-if="loading" />
        <ViolationHistory v-else :records="records" />
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import {
  GET_LIST_REASON_CHEAT,
  GET_LIST_STUDENT_CHEAT,
  GET_STUDENT_CHEATING_LOG,
  STUDENT_CHEATING_OR_VIOLATION
} from '@/core/services/store/marking/marking.module';
import { FRAUD, REGULATION, VIOLATION_SKILLS } from '@/core/option/examViolation';
import ViolationForm from './ViolationForm.vue';
import ViolationFormSkeleton from './ViolationFormSkeleton.vue';
import ViolationHistory from './ViolationHistory.vue';
import ViolationHistorySkeleton from './ViolationHistorySkeleton.vue';

export default {
  name: 'ExamViolation',
  components: { ViolationForm, ViolationFormSkeleton, ViolationHistory, ViolationHistorySkeleton },
  props: {
    markingExamShiftId: { type: Number, default: null },
    examForm: { type: [Number, String], default: null },
    // Dự phòng cho UI thi đầu khóa; hiện chưa thay đổi flow xử lý.
    isEntrance: { type: Boolean, default: false }
  },
  data() {
    return {
      activeTab: 'record', students: [], reasons: [], records: [],
      loading: true, submitting: false, loadError: '',
      loadingReasons: false, reasonError: '', reasonQuery: null, reasonRequestId: 0
    };
  },
  mounted() {
    this.loadData();
  },
  beforeDestroy() {
    this.reasonRequestId++;
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.loadError = '';
      try {
        const params = { marking_exam_shift_id: this.markingExamShiftId };
        const [students, records] = await Promise.all([
          this.$store.dispatch(GET_LIST_STUDENT_CHEAT, params), this.fetchHistory()
        ]);
        if (students.error) throw new Error('Không tải được thông tin vi phạm.');
        this.students = (students.data || []).map(student => ({
          ...student,
          student_code: student.student_code || (student.user_id ? +student.user_id + 100000 : ''),
          phone: student.phone || student.user_phone || '',
          classroom_code: student.classroom_code || student.classroom_id || '',
          exam_form: student.exam_form || this.examForm
        }));
        this.$emit('examName', this.students.length ? this.students[0].shift_name : '');
        this.records = records;
      } catch (error) {
        this.loadError = error.message || 'Không tải được thông tin vi phạm.';
      } finally {
        this.loading = false;
      }
    },
    async fetchHistory() {
      const res = await this.$store.dispatch(GET_STUDENT_CHEATING_LOG, {
        marking_exam_shift_id: this.markingExamShiftId,
        per_page: 100,
        is_all: true
      });
      if (res.error) throw new Error(res.message || 'Không tải được lịch sử xử lý.');
      return (res.data || []).map(this.normalizeHistoryRecord);
    },
    normalizeHistoryRecord(record) {
      const isFraud = +record.type === 0;
      return {
        id: record.id,
        marking_exam_shift_id: record.marking_exam_shift_id,
        student: {
          user_name: record.user_name,
          student_code: record.user_id,
          phone: record.user_phone,
          classroom_code: record.classroom_name
        },
        violation_type: isFraud ? FRAUD : REGULATION,
        violation_type_name: record.type_label || (isFraud ? 'Vi phạm gian lận' : 'Vi phạm quy chế'),
        reasons: (record.cheating_reason_id || []).map(reason => ({
          id: reason.id,
          reason: reason.name
        })),
        skills: (record.skills || []).map(skill => ({
          ...skill,
          name: skill.name || VIOLATION_SKILLS.find(item => item.id === skill.id)?.name
        })),
        description: record.description,
        evidence: record.evidence || 'Không có',
        handling_record: record.info_process || 'Không có',
        created_by: record.created_user_name || 'Không có',
        created_at: record.created_at
      };
    },
    async loadReasons(query) {
      const requestId = ++this.reasonRequestId;
      this.reasonQuery = query;
      this.reasons = [];
      this.reasonError = '';
      this.loadingReasons = false;
      if (!query || !query.violation_type) return;
      this.loadingReasons = true;
      try {
        const res = await this.$store.dispatch(GET_LIST_REASON_CHEAT, {
          marking_exam_shift_id: this.markingExamShiftId
        });
        if (requestId !== this.reasonRequestId) return;
        if (res.error) throw new Error(res.message || 'Không tải được hành vi vi phạm.');
        const reasonGroup = query.violation_type === FRAUD ? 'gian_lan' : 'vi_pham_quy_che';
        this.reasons = res.data?.[reasonGroup] || [];
      } catch (error) {
        if (requestId === this.reasonRequestId) this.reasonError = error.message || 'Không tải được hành vi vi phạm.';
      } finally {
        if (requestId === this.reasonRequestId) this.loadingReasons = false;
      }
    },
    async recordViolation(form) {
      if (this.submitting || this.loadingReasons || this.reasonError) return;
      this.submitting = true;
      try {
        const res = await this.$store.dispatch(STUDENT_CHEATING_OR_VIOLATION, {
          marking_exam_shift_id: this.markingExamShiftId,
          user_id: form.user_id,
          cheating_reason_id: form.reason_ids,
          description: form.description || null,
          type: form.violation_type === FRAUD ? 0 : 1,
          skills: form.violation_type === REGULATION ? form.skill_ids : [],
          evidence: form.evidence,
          info_process: form.handling_record
        });
        if (res.error) throw new Error(res.message || 'Không thể ghi nhận vi phạm.');
        this.$refs.violationForm.resetForm();
        this.$message.success(res.message || 'Ghi nhận vi phạm thành công!');
        try {
          this.records = await this.fetchHistory();
        } catch (error) {
          this.$message.warning(error.message || 'Chưa thể cập nhật lịch sử xử lý.');
        }
      } catch (error) {
        this.$message.error(error.data?.message || error.message || 'Không thể ghi nhận vi phạm. Vui lòng thử lại.');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.exam-violation { color: #303133; font-size: 14px; line-height: 22px; word-break: normal; }
.load-error { color: #f56c6c; font-size: 14px; line-height: 22px; }
.exam-violation ::v-deep .el-tabs__item { height: 40px; padding: 0 16px; color: #596579; font-size: 14px; font-weight: 500; line-height: 40px; }
.exam-violation ::v-deep .el-tabs__item.is-active { color: #6C3EF4; font-weight: 600; }
.exam-violation ::v-deep .el-tabs__active-bar { background-color: #6C3EF4; height: 2px; }
.exam-violation ::v-deep .el-tabs__nav-wrap::after { height: 1px; background-color: #e8edf5; }
.exam-violation ::v-deep .el-tabs__header { width: 88%; margin: 0 0 14px 18px; }
.exam-violation ::v-deep .el-tabs__content { overflow: visible; }
</style>
